import { Dialog, DialogActions, DialogContent, DialogTitle, Icon, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import { OrganizationOpportunityStatus } from "generated/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { capitalCase } from "change-case";
import useUpdateOpportunityStatusesOrdering from "hooks/opportunities/useUpdateOpportunityStatusesOrdering";
import useCreateOpportunityStatus from "hooks/opportunities/useCreateOpportunityStatus";

interface ManagePipelineOrderModalProps {
  open: boolean;
  onClose: () => void;
  statuses: OrganizationOpportunityStatus[] | null;
}

export default function ManagePipelineOrderModal({
  open,
  onClose,
  statuses,
}: ManagePipelineOrderModalProps) {
  const [updateOrdering, { loading, error }] = useUpdateOpportunityStatusesOrdering();
  const [createOpportunityStatus, createStatusContext] = useCreateOpportunityStatus();
  const orderedStatuses = useMemo(() => {
    return (statuses ?? []).slice().sort((a, b) => a.ordering - b.ordering);
  }, [statuses]);
  const [localStatuses, setLocalStatuses] = useState(orderedStatuses);
  const [isCreatingStatus, setIsCreatingStatus] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");

  useEffect(() => {
    setLocalStatuses(orderedStatuses);
  }, [orderedStatuses]);

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) {
        return;
      }
      const items = Array.from(localStatuses);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      setLocalStatuses(items);
    },
    [localStatuses]
  );

  const handleSave = useCallback(async () => {
    const payload = localStatuses.map((status, index) => ({
      id: status.id,
      ordering: index + 1,
    }));
    await updateOrdering(payload);
    onClose();
  }, [localStatuses, onClose, updateOrdering]);

  const handleCreateStatus = useCallback(async () => {
    const trimmedName = newStatusName.trim();
    if (!trimmedName) {
      return;
    }

    const result = await createOpportunityStatus({
      variables: { name: trimmedName },
    });
    const createdStatus = result?.data?.createOpportunityStatus;
    if (createdStatus) {
      setLocalStatuses((prev) =>
        [...prev, createdStatus].sort((a, b) => a.ordering - b.ordering)
      );
      setNewStatusName("");
      setIsCreatingStatus(false);
    }
  }, [createOpportunityStatus, newStatusName]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <span>Manage Pipeline Order</span>
          <MDButton
            className="tour-add-pipeline-step"
            color="success"
            size="small"
            onClick={() => setIsCreatingStatus((prev) => !prev)}
            sx={{ minWidth: 0, width: 28, height: 28, p: 0, borderRadius: "999px" }}
          >
            <Icon fontSize="small">add</Icon>
          </MDButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        {(error || createStatusContext.error) && (
          <MDAlert color="error">
            {error ? "Unable to update pipeline order." : "Unable to create status."}
          </MDAlert>
        )}
        {isCreatingStatus && (
          <MDBox mt={1} mb={2} display="flex" alignItems="center" gap={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Status name"
              value={newStatusName}
              onChange={(event) => setNewStatusName(event.target.value)}
            />
            <MDButton
              color="success"
              size="small"
              onClick={handleCreateStatus}
              disabled={createStatusContext.loading || !newStatusName.trim()}
            >
              Add
            </MDButton>
          </MDBox>
        )}
        <MDBox mt={1}>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="pipeline-statuses">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {localStatuses.map((status, index) => (
                    <Draggable key={status.id} draggableId={status.id} index={index}>
                      {(draggableProvided) => (
                        <MDBox
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            padding: "10px 12px",
                            marginBottom: "8px",
                          }}
                        >
                          <MDTypography variant="body">
                            {capitalCase(status.name)}
                          </MDTypography>
                          <Icon fontSize="small" sx={{ color: "#9e9e9e" }}>
                            drag_indicator
                          </Icon>
                        </MDBox>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton
          className="tour-pipeline-order-cancel"
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </MDButton>
        <MDButton color="blue" onClick={handleSave} disabled={loading}>
          Save Order
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
